class PanelConfigurator extends HTMLElement {
  constructor() {
    super();
    this.variants = [];
    this.currentVariantId = null;
    this.elements = {};
    this.quantity = 1;
    this._adding = false;
  }

  connectedCallback() {
    this.loadVariants();
    this.cacheElements();
    this.currentVariantId = this.detectCurrentVariantId();
    this.bindEvents();
    this.handleInitialExpansion();
    this.setQuantity(1);
  }

  loadVariants() {
    const dataEl = this.querySelector('script[data-variants]');
    if (!dataEl) return;
    try {
      this.variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.warn('[panel-configurator] failed to parse variants', e);
      this.variants = [];
    }
  }

  cacheElements() {
    this.elements = {
      toggle: this.querySelector('[data-toggle]'),
      body: this.querySelector('[data-body]'),
      heightInput: this.querySelector('[data-input="height"]'),
      widthInput: this.querySelector('[data-input="width"]'),
      recommendation: this.querySelector('[data-recommendation]'),
      recommendationText: this.querySelector('[data-recommendation-text]'),
      accessories: Array.from(this.querySelectorAll('[data-accessory]')).map((row) => ({
        checkbox: row.querySelector('[data-accessory-checkbox]'),
        qtyEl: row.querySelector('[data-accessory-qty]'),
        variantId: parseInt(row.dataset.variantId, 10),
        perPanels: parseInt(row.dataset.perPanels, 10) || 0,
        price: parseInt(row.dataset.price, 10) || 0,
      })),
      coverage: this.querySelector('[data-coverage]'),
      qtyInput: this.querySelector('[data-qty-input]'),
      qtyDecrease: this.querySelector('[data-qty-decrease]'),
      qtyIncrease: this.querySelector('[data-qty-increase]'),
      atc: this.querySelector('[data-atc]'),
      checkout: this.querySelector('[data-checkout]'),
      atcTotal: this.querySelector('[data-atc-total]'),
      atcError: this.querySelector('[data-atc-error]'),
      klarnaAmount: this.querySelector('[data-klarna-amount]'),
    };
  }

  showError(msg) {
    if (!this.elements.atcError) return;
    this.elements.atcError.textContent = msg || '';
    this.elements.atcError.hidden = !msg;
  }

  // 'grid' (rows x columns of rectangular panels) or 'hexagon' (honeycomb, area based).
  get calcMode() {
    return this.dataset.calcMode === 'hexagon' ? 'hexagon' : 'grid';
  }

  // Wall area one panel really covers, in cm². A hexagon's bounding box (width x height)
  // is 25% larger than the hexagon itself, and in a honeycomb the hexagons interlock,
  // so the true area is 0.75 x width x height.
  panelAreaCm2(variant) {
    const panelW = this.variantDimToCm(variant.width, variant.width_unit);
    const panelH = this.variantDimToCm(variant.height, variant.height_unit);
    if (panelW === 0 || panelH === 0) return 0;
    return panelW * panelH * (this.calcMode === 'hexagon' ? 0.75 : 1);
  }

  detectCurrentVariantId() {
    const input = document.querySelector('[name="id"]');
    if (input && input.value) return parseInt(input.value, 10);
    return this.variants[0] ? this.variants[0].id : null;
  }

  getCurrentVariant() {
    return this.variants.find(v => v.id === this.currentVariantId) || this.variants[0];
  }

  bindEvents() {
    if (this.elements.toggle) {
      this.elements.toggle.addEventListener('click', () => this.toggleBody());
    }
    if (this.elements.heightInput) {
      this.elements.heightInput.addEventListener('input', () => this.recalc());
    }
    if (this.elements.widthInput) {
      this.elements.widthInput.addEventListener('input', () => this.recalc());
    }
    if (this.elements.qtyDecrease) {
      this.elements.qtyDecrease.addEventListener('click', () => this.setQuantity(this.getQuantity() - 1));
    }
    if (this.elements.qtyIncrease) {
      this.elements.qtyIncrease.addEventListener('click', () => this.setQuantity(this.getQuantity() + 1));
    }
    if (this.elements.qtyInput) {
      this.elements.qtyInput.addEventListener('change', () => this.setQuantity(this.getQuantity()));
    }
    this.elements.accessories.forEach((acc) => {
      if (acc.checkbox) acc.checkbox.addEventListener('change', () => this.updateSummary());
    });
    if (this.elements.atc) {
      this.elements.atc.addEventListener('click', (e) => this.addToCart(e.currentTarget));
    }
    if (this.elements.checkout) {
      this.elements.checkout.addEventListener('click', (e) => this.addToCart(e.currentTarget, { checkout: true }));
    }
    document.addEventListener('variant:change', (e) => {
      const v = e && e.detail && e.detail.variant;
      this.currentVariantId = v ? v.id : this.detectCurrentVariantId();
      this.recalc();
      this.updateSummary();
    });
  }

  handleInitialExpansion() {
    const expanded = this.dataset.expanded === 'true';
    if (this.elements.body) this.elements.body.hidden = !expanded;
    if (this.elements.toggle) this.elements.toggle.setAttribute('aria-expanded', String(expanded));
  }

  toggleBody(force) {
    if (!this.elements.body) return;
    const open = force !== undefined ? force : this.elements.body.hidden;
    this.elements.body.hidden = !open;
    if (this.elements.toggle) this.elements.toggle.setAttribute('aria-expanded', String(open));
  }

  parseInput(input) {
    if (!input) return 0;
    const value = parseFloat(input.value);
    if (isNaN(value) || value <= 0) return 0;
    return value;
  }

  // Normalize a variant dimension (value + Shopify unit enum) to cm.
  variantDimToCm(value, unit) {
    if (!value || value <= 0) return 0;
    const u = String(unit || 'CENTIMETERS').toUpperCase();
    if (u === 'MILLIMETERS') return value / 10;
    if (u === 'METERS') return value * 100;
    if (u === 'INCHES') return value * 2.54;
    return value; // CENTIMETERS or unknown -> assume cm
  }

  // Convert a user input value (cm or inch per theme setting) to cm.
  inputToCm(value) {
    const unit = this.dataset.defaultUnit || 'cm';
    return unit === 'inch' ? value * 2.54 : value;
  }

  recalc() {
    const wallH = this.inputToCm(this.parseInput(this.elements.heightInput));
    const wallW = this.inputToCm(this.parseInput(this.elements.widthInput));

    this.updateRecommendation(wallH);

    if (wallH === 0 || wallW === 0) return; // keep current quantity until both are set

    const variant = this.getCurrentVariant();
    if (!variant) return;

    if (this.calcMode === 'hexagon') {
      const area = this.panelAreaCm2(variant);
      if (area === 0) return;
      this.setQuantity(Math.ceil((wallW * wallH) / area));
      return;
    }

    const panelW = this.variantDimToCm(variant.width, variant.width_unit);
    const panelH = this.variantDimToCm(variant.height, variant.height_unit);
    if (panelW === 0 || panelH === 0) return;

    const cols = Math.ceil(wallW / panelW);
    const rows = Math.ceil(wallH / panelH);
    this.setQuantity(cols * rows);
  }

  updateRecommendation(wallHcm) {
    const el = this.elements.recommendation;
    if (!el) return;

    // Standard lengths only exist for rectangular panels.
    if (this.calcMode === 'hexagon') {
      el.hidden = true;
      return;
    }

    const variant = this.getCurrentVariant();
    const currentLen = variant ? this.variantDimToCm(variant.height, variant.height_unit) : 0;

    // Only when a wall height is entered that exceeds the CURRENT panel length,
    // and a longer standard length actually exists to point to.
    if (!wallHcm || wallHcm <= 0 || !currentLen || wallHcm <= currentLen) {
      el.hidden = true;
      return;
    }

    const longer = (this.dataset.standardLengths || '240,300')
      .split(',').map(s => parseFloat(s.trim())).filter(n => n > 0).sort((a, b) => a - b)
      .filter(l => l > currentLen);
    if (!longer.length) { el.hidden = true; return; } // current panel is already the longest

    // smallest longer length that covers the wall height, else the longest available
    const rec = longer.find(l => l >= wallHcm) || longer[longer.length - 1];

    const tpl = (window.AKU_I18N && window.AKU_I18N.recommend) || 'Based on the specified height, we recommend panels of %length% cm';
    if (this.elements.recommendationText) {
      this.elements.recommendationText.textContent = tpl.replace('%length%', rec);
    }
    el.hidden = false;
  }

  getQuantity() {
    const v = parseInt(this.elements.qtyInput && this.elements.qtyInput.value, 10);
    return (isNaN(v) || v < 1) ? 1 : v;
  }

  setQuantity(n) {
    n = Math.max(1, Math.floor(n) || 1);
    this.quantity = n;
    if (this.elements.qtyInput) this.elements.qtyInput.value = n;
    this.updateSummary();
  }

  accessoryQty(panelQty, perPanels) {
    if (!perPanels || perPanels <= 0) return 1;
    return Math.max(1, Math.ceil(panelQty / perPanels));
  }

  updateSummary() {
    const variant = this.getCurrentVariant();
    const qty = this.getQuantity();

    if (this.elements.coverage && variant) {
      const area = (this.panelAreaCm2(variant) * qty) / 10000;
      const tpl = (window.AKU_I18N && window.AKU_I18N.coverage) || 'Covers %area% m²';
      this.elements.coverage.textContent = area > 0 ? tpl.replace('%area%', area.toFixed(2)) : '';
    }

    const accessoriesTotal = this.elements.accessories.reduce((sum, acc) => {
      if (!acc.checkbox || !acc.checkbox.checked) return sum;
      return sum + acc.price * this.accessoryQty(qty, acc.perPanels);
    }, 0);

    if (this.elements.atcTotal && variant) {
      this.elements.atcTotal.textContent = this.formatMoney(variant.price * qty + accessoriesTotal);
    }

    if (this.elements.klarnaAmount && variant) {
      const installments = parseInt(this.dataset.klarnaInstallments, 10) || 3;
      this.elements.klarnaAmount.textContent = this.formatMoney((variant.price * qty + accessoriesTotal) / installments);
    }

    this.elements.accessories.forEach((acc) => {
      if (acc.qtyEl) acc.qtyEl.textContent = this.accessoryQty(qty, acc.perPanels);
    });
  }

  formatMoney(cents) {
    const fmt = (window.AKU_I18N && window.AKU_I18N.money_format)
      || (window.theme && theme.settings && theme.settings.moneyFormat)
      || '€{{amount}}';
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, fmt);
    }
    const amount = (cents / 100).toFixed(2);
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  setBusy(busy, trigger) {
    [this.elements.atc, this.elements.checkout].forEach((btn) => {
      if (!btn) return;
      btn.toggleAttribute('disabled', busy);
      btn.toggleAttribute('aria-busy', busy && (!trigger || btn === trigger));
    });
  }

  async addToCart(trigger, opts) {
    if (this._adding) return;
    const toCheckout = !!(opts && opts.checkout);
    const variant = this.getCurrentVariant();
    if (!variant) return;

    const qty = this.getQuantity();
    const items = [{ id: variant.id, quantity: qty }];

    this.elements.accessories.forEach((acc) => {
      if (!acc.variantId || !acc.checkbox || !acc.checkbox.checked) return;
      items.push({ id: acc.variantId, quantity: this.accessoryQty(qty, acc.perPanels) });
    });

    const routes = (window.theme && theme.routes) || {};
    const addUrl = routes.cart_add_url || '/cart/add.js';
    const cartUrl = routes.cart_url || '/cart.js';

    this._adding = true;
    this.setBusy(true, trigger);
    this.showError('');
    let redirecting = false;

    try {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));

      const config = (window.theme && theme.utils && theme.utils.fetchConfig)
        ? theme.utils.fetchConfig('javascript')
        : { method: 'POST', headers: { Accept: 'application/javascript' } };
      config.method = 'POST';
      config.headers = config.headers || {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.headers['Content-Type'] = 'application/json';
      config.body = JSON.stringify({ items, sections, sections_url: window.location.pathname });

      const res = await fetch(addUrl, config);
      const parsed = await res.json();

      if (parsed.status) {
        // e.g. 422 when stock/quantity rules cap the request. Surface the message and
        // refresh the cart badge (Shopify may have partially added what was available).
        this.showError(parsed.description || parsed.message || '');
        try {
          const cj = await (await fetch(cartUrl, { method: 'GET', headers: { Accept: 'application/json' } })).json();
          if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'panel-configurator', cart: cj });
          }
        } catch (e) { /* non-fatal */ }
        return;
      }

      if (toCheckout) {
        redirecting = true;
        window.location.href = '/checkout';
        return;
      }

      let cartJson = null;
      try {
        cartJson = await (await fetch(cartUrl, { method: 'GET', headers: { Accept: 'application/json' } })).json();
      } catch (e) { /* non-fatal */ }
      if (cartJson) cartJson.sections = parsed.sections;

      if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'panel-configurator',
          productVariantId: variant.id,
          cart: cartJson,
        });
      }
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsed } }));

      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.show === 'function') {
        drawer.show(trigger);
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
      }
    } catch (e) {
      console.warn('[panel-configurator] add to cart failed', e);
    } finally {
      if (!redirecting) {
        this._adding = false;
        this.setBusy(false);
      }
    }
  }
}

if (!customElements.get('panel-configurator')) {
  customElements.define('panel-configurator', PanelConfigurator);
}
